import '@chewy/kib-form-styles/dist/styles/kib-form-dropdown.css';

import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { KibInputSelect } from '@chewy/kib-fields-react';
import { PdpMdaAttributeFragment, PdpMdaItemFragment } from './__generated__/MDA';
import { useMDA } from './MDAProvider';
import styles from './styles.module.scss';
import { KibFormDropdown } from '@chewy/kib-form-react';
import { KibRadio } from '@chewy/kib-choices-react';
import { useTranslation } from 'react-i18next';
import { KibSheet } from '@chewy/kib-overlays-react';
import { KibSelectionCard } from '@chewy/kib-cards-react';
import { KibProductPrice } from '@chewy/kib-product-react';
import { getPricePerUnit } from './utils';
import {
  CLICK_EVENT_ACTION,
  SEGMENT_MDA_ACCORDION_COLLAPSED,
  SEGMENT_MDA_CLICKED_NAME,
  SEGMENT_MDA_DROPDOWN_SELECTOR,
  SEGMENT_MDA_EVENT_CATEGORY,
} from '../../analytics';
import ImpressionEvent from '../../utils/impressionEvent';
import { useAnalyticsContext } from '../../providers/analytics/AnalyticsProvider';

export interface Props {
  attribute: PdpMdaAttributeFragment;
}

export interface SelectorMDAOption {
  /* eslint-disable react/no-unused-prop-types */
  label: string;
  value: string;
  item: PdpMdaItemFragment | null;
}

const DropdownSelector: React.FC<Props> = ({ attribute }) => {
  const {
    changeAttributeOptions,
    selectedAttributeOptions,
    getItemByAttributeOptions,
    replaceOptionByAttribute,
  } = useMDA();
  const { t } = useTranslation();
  const { trackSegmentEvent } = useAnalyticsContext();
  // get this attribute's selected option from the mda provider state
  const selectedOptionId = selectedAttributeOptions.find(
    (attrOption) => attrOption.attributeId === attribute.id,
  )?.optionId;
  const [value, setValue] = useState(selectedOptionId);
  const [showMobileOptionSheet, setShowMobileOptionSheet] = useState(false);
  const selectedOptionSheetRef = useRef<HTMLDivElement>(null);
  const selectedOptionDropdownRef = useRef<HTMLInputElement>(null);

  // when matching attribute selected options from the provider changes, update value
  useEffect(() => {
    const selectedOptionId = selectedAttributeOptions.find(
      (attrOption) => attrOption.attributeId === attribute.id,
    )?.optionId;
    if (value !== selectedOptionId) setValue(selectedOptionId);
    // only update selected value when selected attributes are change by provider
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAttributeOptions]);

  // construct options in the format that KibFormDropdown needs
  const options: SelectorMDAOption[] =
    attribute.options?.map((option) => {
      // get attribute options of this current option
      const currentAttributeOptions = replaceOptionByAttribute(attribute, option);
      // get item by this set of attribute options
      const currentItem = getItemByAttributeOptions(currentAttributeOptions);

      return {
        label: option?.value ?? '',
        value: option?.value ?? '',
        item: currentItem,
      };
    }) ?? [];

  const trackDropdownSelect = (value: string) => {
    trackSegmentEvent(SEGMENT_MDA_CLICKED_NAME, {
      eventCategory: SEGMENT_MDA_EVENT_CATEGORY,
      eventAction: CLICK_EVENT_ACTION,
      eventLabel: value,
    });
  };

  // scroll selected option in KibSheet into view
  const scrollSheetOptionIntoView = () => {
    const option = selectedOptionSheetRef.current;
    const parent = selectedOptionSheetRef.current?.parentElement;
    if (parent && option) {
      parent.scrollTo(0, option.offsetTop - option.clientHeight - 20);
    }
  };

  // scroll selected option in KibFormDropdown into view
  // unfortunately we have to select by kib classes due to this being a legacy component
  const scrollDropdownOptionIntoView = () => {
    const parent = selectedOptionDropdownRef.current?.closest('.kib-form-dropdown__list');
    const radioParent = selectedOptionDropdownRef.current?.closest<HTMLElement>(
      '.kib-form-dropdown__option',
    );
    if (parent && radioParent) {
      parent.scrollTo(0, radioParent.offsetTop - 20);
    }
  };

  if (!attribute.name || !attribute.id || !value) return null;

  return (
    <div data-testid="dropdown-selector">
      {/* fires an impression event for type of selector */}
      <ImpressionEvent
        category={SEGMENT_MDA_EVENT_CATEGORY}
        label={SEGMENT_MDA_DROPDOWN_SELECTOR}
      />

      {/* fires an impression event the attribute */}
      <ImpressionEvent
        category={SEGMENT_MDA_EVENT_CATEGORY}
        label={`mda_${attribute.name.toLowerCase()}`}
      />

      {/*
        For small device widths, we show a KibInputSelect initially,
        but on interaction with it, we show a KibSheet in order to pick an option
      */}
      <div className={styles.mobileDropdown}>
        <KibInputSelect
          data-testid="dropdown-mobile-select"
          label={attribute.name}
          options={options}
          value={value}
          onMouseDown={(e) => {
            if (e.button === 0) {
              e.preventDefault();
              e.currentTarget.blur();
              window.focus();
              setShowMobileOptionSheet(true);
            }
          }}
        />
        {showMobileOptionSheet && (
          <KibSheet
            data-testid={'dropdown-mobile-sheet'}
            open={showMobileOptionSheet}
            bottom
            title={attribute.name}
            onClose={() => setShowMobileOptionSheet(false)}
            onOpen={() => scrollSheetOptionIntoView()}
          >
            {options &&
              options.map((option) => {
                if (!option || !value) return null;

                const unavailableText = t('stock.seeAvailableOptionsText');
                const stockText = option.item?.inStock ? t('stock.inStock') : t('stock.outOfStock');
                const inStockText = !option.item?.isPublished ? unavailableText : stockText;

                return (
                  <React.Fragment key={option?.value}>
                    <KibSelectionCard
                      label={
                        <div>
                          <label
                            htmlFor={`kib-radio__${option.value}`}
                            className={styles.radioChoiceFieldLabel}
                          >
                            <div
                              className={cx(styles.label, {
                                [styles.labelTextLong]: option.label.length >= 25,
                                [styles.labelTextShort]: option.label.length < 25,
                              })}
                            >
                              {option.label}
                            </div>
                            <div
                              className={cx(styles.inStockOptionText, {
                                [styles.outOfStock]:
                                  !option.item?.inStock || !option.item?.isPublished,
                              })}
                            >
                              {inStockText}
                            </div>
                          </label>
                          <KibProductPrice
                            className={styles.priceOptionText}
                            price={option.item?.advertisedPrice ?? undefined}
                            size="sm"
                          />
                          {option.item?.perUnitPrice && (
                            <div className={styles.ppuOptionText}>
                              {getPricePerUnit(
                                option.item?.perUnitPrice,
                                option.item?.unitOfMeasureString,
                              )}
                            </div>
                          )}
                        </div>
                      }
                      selectedValue={value}
                      value={option.value}
                      onChange={(optionId) => {
                        if (optionId) {
                          setValue(optionId.toString());
                          changeAttributeOptions(attribute, optionId?.toString());
                          trackDropdownSelect(`${attribute.name} - ${optionId.toString()}`);
                        }
                        setShowMobileOptionSheet(false);
                      }}
                      ref={option.value === value ? selectedOptionSheetRef : null}
                    />
                  </React.Fragment>
                );
              })}
          </KibSheet>
        )}
      </div>

      {/*
        For md and above device widths, use a legacy kib component called KibFormDropdown with a KibRadio list
        We heavily re-style this component to make it resemble a Chirp designed component
      */}
      <div className={cx(styles.desktopDropdown)}>
        <KibFormDropdown
          className={cx(styles.dropdownSelector)}
          label={attribute.name}
          options={options}
          value={value}
          input={(optionId) => {
            setValue(optionId.toString());
            changeAttributeOptions(attribute, optionId as string);
            trackSegmentEvent(SEGMENT_MDA_ACCORDION_COLLAPSED, {
              eventCategory: SEGMENT_MDA_EVENT_CATEGORY,
              eventAction: CLICK_EVENT_ACTION,
              eventLabel: 'close',
            });
            trackDropdownSelect(`${attribute.name} - ${optionId.toString()}`);
          }}
          onBlur={() => scrollDropdownOptionIntoView()}
        >
          {/* @ts-ignore KibFormDropdown is a legacy and unsupported Kib Component whose typings are incompatible with current React versions */}
          {({ value: optionValue, label, item }: SelectorMDAOption) => {
            const unavailableText = t('stock.seeAvailableOptionsText');
            const stockText = item?.inStock ? t('stock.inStock') : t('stock.outOfStock');
            const inStockText = !item?.isPublished ? unavailableText : stockText;
            return (
              <>
                <KibRadio
                  label={label}
                  checked={value === optionValue}
                  data-testid="dropdown-radio-option"
                  ref={optionValue === value ? selectedOptionDropdownRef : null}
                />

                <span
                  className={cx(
                    item?.inStock || !item?.isPublished ? styles.inStock : styles.outOfStock,
                  )}
                >
                  {!item?.isPublished ? unavailableText : inStockText}
                </span>
              </>
            );
          }}
        </KibFormDropdown>
      </div>
    </div>
  );
};

export default DropdownSelector;
